(() => {
  const ATTRIBUTION_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
    "gbraid",
    "wbraid",
    "fbclid",
    "msclkid",
  ];
  const EVENT_KEYS = new Set([
    "page_type",
    "page_path",
    "source_component",
    "product_title",
    "product_handle",
    "collection_title",
    "collection_handle",
    "landing_page",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
    "gbraid",
    "wbraid",
    "fbclid",
    "msclkid",
    "form_id",
    "error_type",
    "file_type",
  ]);
  const EVENT_PARAMETER_KEYS = new Map([
    ["newsletter_signup", new Set([
      "page_type",
      "page_path",
      "source_component",
      "landing_page",
      ...ATTRIBUTION_KEYS,
    ])],
    ["quote_form_expand_optional", new Set([
      "page_type",
      "page_path",
      "source_component",
      "form_id",
    ])],
  ]);
  const FIRST_TOUCH_KEY = "rj_attribution_first";
  const LAST_TOUCH_KEY = "rj_attribution_last";
  const LEAD_TOKEN_KEY = "rj_quote_lead_token";
  const LEAD_TOKEN_MAX_AGE_MS = 30 * 60 * 1000;

  const getStorage = () => {
    try {
      return window.sessionStorage;
    } catch (error) {
      return null;
    }
  };

  const readJson = (key) => {
    const storage = getStorage();
    if (!storage) return null;
    try {
      const value = storage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  };

  const writeJson = (key, value) => {
    const storage = getStorage();
    if (!storage) return false;
    try {
      storage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  };

  const removeValue = (key) => {
    const storage = getStorage();
    if (!storage) return;
    try {
      storage.removeItem(key);
    } catch (error) {
      return;
    }
  };

  const readAttributionParameters = (locationValue = window.location) => {
    const parameters = new URLSearchParams(locationValue.search || "");
    return ATTRIBUTION_KEYS.reduce((values, key) => {
      values[key] = parameters.get(key) || "";
      return values;
    }, {});
  };

  const createTouch = (locationValue = window.location, referrer = document.referrer || "", now = Date.now()) => {
    const parameters = readAttributionParameters(locationValue);
    return {
      ...parameters,
      landing_page: `${locationValue.pathname || "/"}${locationValue.search || ""}`,
      referrer,
      captured_at: new Date(now).toISOString(),
    };
  };

  const hasAdParameters = (touch) => ATTRIBUTION_KEYS.some((key) => Boolean(touch && touch[key]));

  const captureAttribution = () => {
    const currentTouch = createTouch();
    const firstTouch = readJson(FIRST_TOUCH_KEY);
    const lastTouch = readJson(LAST_TOUCH_KEY);

    if (!firstTouch) {
      writeJson(FIRST_TOUCH_KEY, currentTouch);
      writeJson(LAST_TOUCH_KEY, currentTouch);
      return { first: currentTouch, last: currentTouch };
    }

    if (hasAdParameters(currentTouch)) {
      writeJson(LAST_TOUCH_KEY, currentTouch);
      return { first: firstTouch, last: currentTouch };
    }

    return { first: firstTouch, last: lastTouch || firstTouch };
  };

  const getAttribution = () => {
    const first = readJson(FIRST_TOUCH_KEY);
    const last = readJson(LAST_TOUCH_KEY);
    return {
      first: first || null,
      last: last || first || null,
    };
  };

  const getFormAttribution = () => {
    const attribution = getAttribution();
    const output = {};
    for (const prefix of ["first", "last"]) {
      const touch = attribution[prefix] || {};
      for (const key of ATTRIBUTION_KEYS) output[`${prefix}_${key}`] = touch[key] || "";
      output[`${prefix}_landing_page`] = touch.landing_page || "";
      output[`${prefix}_referrer`] = touch.referrer || "";
    }
    return output;
  };

  const getPageContext = () => ({
    page_type: document.body && document.body.dataset ? document.body.dataset.pageType || "" : "",
    page_path: document.body && document.body.dataset
      ? document.body.dataset.pagePath || window.location.pathname || ""
      : window.location.pathname || "",
  });

  const getEventAttribution = () => {
    const { last } = getAttribution();
    if (!last) return {};
    const output = { landing_page: last.landing_page || "" };
    for (const key of ATTRIBUTION_KEYS) output[key] = last[key] || "";
    return output;
  };

  const normalizeParameters = (parameters = {}) => ({
    ...parameters,
    source_component: parameters.source_component || parameters.source || "",
    product_title: parameters.product_title || parameters.productTitle || "",
    product_handle: parameters.product_handle || parameters.productHandle || "",
    collection_title: parameters.collection_title || parameters.collectionTitle || "",
    collection_handle: parameters.collection_handle || parameters.collectionHandle || "",
  });

  const sanitizeEventParameters = (parameters, allowedKeys = EVENT_KEYS) => Object.entries(parameters).reduce((output, [key, value]) => {
    if (!allowedKeys.has(key) || value === "" || value === null || typeof value === "undefined") return output;
    output[key] = value;
    return output;
  }, {});

  const track = (eventName, parameters = {}) => {
    if (!eventName || typeof eventName !== "string") return null;
    const normalized = normalizeParameters(parameters);
    const rawEventParameters = {
      ...getPageContext(),
      ...getEventAttribution(),
      ...normalized,
    };
    const allowedKeys = EVENT_PARAMETER_KEYS.get(eventName) || EVENT_KEYS;
    const eventParameters = sanitizeEventParameters(rawEventParameters, allowedKeys);
    const customEventDetail = EVENT_PARAMETER_KEYS.has(eventName)
      ? { ...eventParameters }
      : { ...parameters, ...eventParameters };

    window.dispatchEvent(new CustomEvent(eventName, { detail: customEventDetail }));
    window.dataLayer = window.dataLayer || [];
    const payload = { event: eventName, ...eventParameters };
    window.dataLayer.push(payload);
    return payload;
  };

  const createLeadToken = (now = Date.now()) => {
    const token = window.crypto && typeof window.crypto.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `${now}-${Math.random().toString(36).slice(2)}`;
    writeJson(LEAD_TOKEN_KEY, {
      token,
      created_at: now,
      consumed: false,
    });
    return token;
  };

  const getLeadToken = () => {
    const submission = readJson(LEAD_TOKEN_KEY);
    if (!submission || submission.consumed) return "";
    return submission.token || "";
  };

  const consumeLeadToken = (token, now = Date.now()) => {
    if (!token) return false;
    const submission = readJson(LEAD_TOKEN_KEY);
    if (!submission || submission.consumed || submission.token !== token) return false;
    if (!submission.created_at || now - submission.created_at > LEAD_TOKEN_MAX_AGE_MS) {
      removeValue(LEAD_TOKEN_KEY);
      return false;
    }
    submission.consumed = true;
    submission.consumed_at = now;
    writeJson(LEAD_TOKEN_KEY, submission);
    return true;
  };

  window.RJTracking = {
    captureAttribution,
    createLeadToken,
    consumeLeadToken,
    getAttribution,
    getFormAttribution,
    getLeadToken,
    track,
  };
})();
